import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { BotonCerrarHoja } from '#components/common/ModalHoja';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Importe } from '#components/mobile/ui/Importe';
import {
  TecladoImporte,
  useImporteTecleado,
} from '#components/mobile/ui/TecladoImporte';
import { color, densidad, num } from '#components/mobile/ui/tokens';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { SheetNameProvider } from '#hooks/useSheetName';
import { useSheetValue } from '#hooks/useSheetValue';
import { envelopeBudget } from '#spreadsheet/bindings';

import { importeReservaInicial, puedeReservar } from './hojasPlan';

type HojaReservarProps = {
  month: string;
  onSubmit: (amount: number) => void;
  /** «Quitar la reserva» (`reset-hold`). */
  onQuitar?: () => void;
  onCerrar: () => void;
};

/**
 * «Reservar para {mes}» (B6 · H37): una sola hoja con el importe (por
 * defecto, lo que queda por asignar; se escribe en el campo grande),
 * «Reservar» y, si ya hay algo reservado, «Quitar la reserva». Sustituye en
 * el móvil a `hold-buffer`.
 */
export function HojaReservar(props: HojaReservarProps) {
  return (
    <SheetNameProvider name={monthUtils.sheetForMonth(props.month)}>
      <HojaReservarContenido {...props} />
    </SheetNameProvider>
  );
}

function HojaReservarContenido({
  month,
  onSubmit,
  onQuitar,
  onCerrar,
}: HojaReservarProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const listo =
    useSheetValue<'envelope-budget', 'to-budget'>(envelopeBudget.toBudget) ?? 0;
  const reservado =
    useSheetValue<'envelope-budget', 'buffered'>(
      envelopeBudget.manualBuffered,
    ) ?? 0;
  // Hasta que se teclea, el importe sigue a lo que queda por asignar (la
  // hoja de cálculo puede llegar un instante después de abrir) y la primera
  // tecla lo sustituye.
  const { escrito, centimos, pulsar } = useImporteTecleado(null);
  const [tocado, setTocado] = useState(false);
  const sugerido = importeReservaInicial(listo);
  const importe = tocado ? centimos : sugerido;
  const siguiente = monthUtils.format(
    monthUtils.nextMonth(month),
    'MMMM',
    locale,
  );
  const titulo = t('Hold for {{month}}', { month: siguiente });

  const reservar = () => {
    if (puedeReservar(importe, listo)) {
      onSubmit(importe);
      onCerrar();
    }
  };

  return (
    <Hoja aria-label={titulo} data-testid="hoja-reservar" onCerrar={onCerrar}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Text
          role="heading"
          aria-level={2}
          style={{
            flex: 1,
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: color.fg,
          }}
        >
          {titulo}
        </Text>
        <BotonCerrarHoja onPress={onCerrar} />
      </View>
      <View
        style={{
          alignItems: 'center',
          padding: '10px 8px 12px',
          borderRadius: 20,
          backgroundColor: color.surface2,
          marginBottom: 14,
        }}
      >
        <Text
          data-testid="reservar-importe"
          style={{
            ...num,
            padding: '0 10px',
            borderRadius: 12,
            backgroundColor:
              !tocado && sugerido > 0 ? color.accentSoft : 'transparent',
            color: color.fg,
            fontSize: 40,
            fontWeight: 800,
            letterSpacing: '-0.035em',
            lineHeight: 1.05,
          }}
        >
          {tocado && escrito !== ''
            ? `${escrito} ${format.currency.symbol}`
            : format(importe, 'financial')}
        </Text>
        <Text style={{ ...densidad.pequeno, color: color.fg2, marginTop: 4 }}>
          <Trans>Ready to assign</Trans> <Importe valor={listo} tono="auto" />
          {reservado > 0 && (
            <>
              {' · '}
              <Trans>Already held</Trans> <Importe valor={reservado} />
            </>
          )}
        </Text>
      </View>
      <View style={{ marginBottom: 14 }}>
        <TecladoImporte
          testId="reservar"
          onTecla={k => {
            setTocado(true);
            pulsar(k);
          }}
        />
      </View>
      <View style={{ gap: 8 }}>
        <Boton
          bloque
          data-testid="reservar"
          isDisabled={!puedeReservar(importe, listo)}
          onPress={reservar}
        >
          <Trans>Hold</Trans>
        </Boton>
        {onQuitar && reservado > 0 && (
          <Boton
            bloque
            variante="fantasma"
            data-testid="quitar-reserva"
            onPress={() => {
              onQuitar();
              onCerrar();
            }}
          >
            <Trans>Remove the hold</Trans>
          </Boton>
        )}
      </View>
    </Hoja>
  );
}
